import clsx from 'clsx';
import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Badge as BadgeType, Warning } from '@jobshop/shared';
import { useStationName } from '../api/hooks';

/**
 * Small primitives in the shadcn idiom — composed from Tailwind classes, owned in this
 * repo rather than pulled in as a component library.
 */

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('card', className)}>{children}</div>;
}

export function CardHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-200 px-4 py-3">
      <div>
        <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-xs text-ink-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * The grey pill that jumps between a job's own pages — "Plan", "List", "Parts",
 * "Scans". Deliberately not a `btn-*`: it sits inline beside a job name rather than in
 * a card's actions row, and reads as a signpost instead of an action.
 *
 * Two sizes, and the difference is what the chip is for: `sm` is an aside next to a row
 * in a list, `md` is the header navigation of the page you are on, which is a target
 * you aim at and so wants a bigger one.
 */
export function ChipLink({
  to,
  title,
  size = 'sm',
  className,
  children,
}: {
  to: string;
  title?: string;
  size?: 'sm' | 'md';
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      title={title}
      className={clsx(
        'rounded bg-ink-200 text-ink-600 hover:bg-ink-300 hover:text-ink-800',
        size === 'md' ? 'px-2.5 py-1 text-sm font-medium' : 'px-1.5 py-0.5 text-xs',
        className,
      )}
    >
      {children}
    </Link>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-ink-500">{hint}</span> : null}
    </label>
  );
}

export function Pill({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info';
  title?: string;
}) {
  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        tone === 'neutral' && 'bg-ink-100 text-ink-700',
        tone === 'good' && 'bg-emerald-100 text-emerald-800',
        tone === 'warn' && 'bg-amber-100 text-amber-900',
        tone === 'bad' && 'bg-red-100 text-red-800',
        tone === 'info' && 'bg-sky-100 text-sky-900',
      )}
    >
      {children}
    </span>
  );
}

const BADGE_LABEL: Record<BadgeType['code'], { text: string; tone: 'warn' | 'bad' }> = {
  overrunning: { text: 'Overrunning', tone: 'warn' },
  notStarted: { text: 'Not started', tone: 'warn' },
  confirmedSlippage: { text: 'Confirmed slippage', tone: 'bad' },
  atRisk: { text: 'At risk', tone: 'bad' },
};

/** In-app only. Nothing is ever pushed or emailed (SPEC §4). */
export function BadgeChip({ badge }: { badge: BadgeType }) {
  const meta = BADGE_LABEL[badge.code];
  const stationName = useStationName();
  return (
    <Pill tone={meta.tone} title={badge.message}>
      {meta.text}
      {badge.stationKey ? ` · ${stationName(badge.stationKey)}` : ''}
    </Pill>
  );
}

export function WarningChip({ warning }: { warning: Warning }) {
  const stationName = useStationName();
  return (
    <Pill tone="warn" title={warning.message}>
      {warning.code === 'zeroCapacity' && 'No capacity'}
      {warning.code === 'horizonExhausted' && 'Beyond horizon'}
      {warning.code === 'pastDelivery' && 'Past delivery'}
      {warning.code === 'noHours' && 'No hours set'}
      {warning.stationKey ? ` · ${stationName(warning.stationKey)}` : ''}
    </Pill>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-8 text-center text-sm text-ink-500">{children}</p>;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-ink-500" role="status">
      <span className="h-3 w-3 animate-spin rounded-full border-2 border-ink-300 border-t-ink-700" />
      {label}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return (
    <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {message}
    </p>
  );
}

export function Modal({
  open,
  onClose,
  title,
  wide,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** For a dialog whose content is a wide table, not a form's worth of fields. */
  wide?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  /*
   * Focus moves to the dialog when it opens, and never again — a caller that passes an
   * inline `onClose` gives this a new identity on every render, and re-focusing here
   * would yank the caret out of whatever field the user is typing in.
   */
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-4 sm:p-8">
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx('card w-full', wide ? 'max-w-3xl' : 'max-w-lg')}
      >
        <CardHeader
          title={title}
          actions={
            <button type="button" className="btn-ghost" onClick={onClose}>
              Close
            </button>
          }
        />
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
